import type { ReactElement } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import type { Dictionary } from 'lodash';
import { groupBy } from 'lodash';
import { mean } from 'mathjs';
import { Card, CardBody, CardHeader } from 'reactstrap';
import Alert from 'atom-components/alert';
import ChartOverViewTable from 'feature-components/report-parts/chart-overview-table/chart-overview-table';
import ReportFooter from 'feature-components/report-parts/report-footer';
import StanineFooterLegend from 'feature-components/reports/stanine-report/footer-legend/footer-legend';
import UsersWidget from 'feature-components/reports/stanine-report/users-widget/users-widget';
import { pluralise } from 'helpers/string.helpers';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import { useArea } from 'services/assist-api/entities/area/areas';
import type { BaseGender } from 'services/assist-api/entities/gender/genders';
import type { BaseReportResponse } from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import { isFinished } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import { useTest } from 'services/assist-api/entities/test/tests';
import { isError, isInitialLoad } from 'utils/check-query-status';
import { percent2color } from 'utils/percent2color';
import Skeleton from './skeleton';
import './stanine-report.scss';

const MAX_ICONS = 15;
const STANINES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export type MinimalResponse = {
  id: number;
  stanine: number;
  scaleScore: number;
  scaleError: number;
  status: BaseReportResponse['status'];
  testInstanceUser?: {
    id: number;
    firstname?: string;
    lastname?: string;
    gender?: API_JSON;
  };
};

export type Props = {
  isReferenceDataVisible: boolean;
  period: { startDate: string; endDate: string };
  testIds: number[];
  areaId: number;
  queryResponses: {
    queries: UseQueryResult<MinimalResponse[]>[];
    data: MinimalResponse[] | undefined;
  };
  genders?: readonly BaseGender[];
  showIndividualReportLinks?: boolean;
};

export default function StanineReport({
  isReferenceDataVisible,
  period,
  areaId,
  testIds = [],
  queryResponses,
  genders = [],
  showIndividualReportLinks = false
}: Props): ReactElement {
  const responses = queryResponses.data;
  const test = useTest(testIds?.length === 1 ? testIds[0] : undefined);
  const area = useArea(areaId);

  const searchParams = useURLSearchParams();

  const generateAssessmentUserLink = useCallback(
    (id: number) => {
      searchParams.set('type', 'individual');
      searchParams.set('assessmentUserId', `${id}`);

      return `../individual?${searchParams.toString()}`;
    },
    [searchParams]
  );

  const reportDataGrouped = useMemo(() => groupBy(responses, 'stanine'), [responses]);

  const studentsPerIcon = useMemo(() => {
    let studentsPerColumn;
    if (!!genders && genders.length > 1) {
      studentsPerColumn = Object.entries(reportDataGrouped)
        .slice(1, STANINES.length)
        .map((e) =>
          // TODO change this to dynamically figure out what the possible gender groups are
          Math.max(
            ...genders.map(
              ({ id }) =>
                e[1].filter(({ testInstanceUser }) => testInstanceUser?.gender?.id === id).length
            )
          )
        );
    } else {
      studentsPerColumn = Object.entries(reportDataGrouped)
        .slice(1, STANINES.length)
        .map((e) => e[1].length);
    }
    if (!studentsPerColumn.length) {
      return 1;
    }
    const max = Math.max(...studentsPerColumn);
    return Math.ceil(max / MAX_ICONS);
  }, [genders, reportDataGrouped]);

  const overviewTableData = useMemo(() => {
    const responseStanines = responses?.map(({ stanine }) => stanine || 0);
    const meanStanine = responseStanines?.length ? mean(responseStanines).toFixed(1) : 'N/A';
    const finishedStudentsCount = responses?.filter(({ status }) => isFinished(status)).length;

    return [
      {
        label: 'Number of Students (Completed)',
        value: `${finishedStudentsCount} / ${responses?.length} `
      },
      {
        label: 'Mean Stanine',
        value: `${meanStanine}${isReferenceDataVisible ? ' (5)*' : ''}`
      }
    ];
  }, [isReferenceDataVisible, responses]);

  /**
   * We group responses based on stanine
   * this function helps to calculate percentage from multiple groups
   */
  const getPercentage = useCallback(
    (r: Dictionary<MinimalResponse[]>, array: number[]) => {
      let count = 0;
      const totalCount = responses?.length || 1;

      // array with group of stanine numbers
      array.forEach((key) => {
        count += r[key]?.length || 0;
      });

      return Math.round((count * 100) / totalCount);
    },
    [responses?.length]
  );

  const viewRef = useScrollIntoView(
    queryResponses.queries.every((q) => q.isFetched),
    test.isFetched || testIds?.length !== 1,
    area.isFetched
  );

  /*  Display loading / error skeleton */
  if (isInitialLoad(...queryResponses.queries, test, area)) {
    return <Skeleton isLoading />;
  }

  if (isError(...queryResponses.queries, test, area)) {
    return <Skeleton isError />;
  }

  const responsesWithNAStanines = responses?.filter((response) => response.stanine === 0);

  return (
    <Card
      innerRef={viewRef}
      className={clsx('stanine-report my-0 ms-0 flex-grow-1', area.data?.json.identifier)}
    >
      <CardHeader className="d-flex justify-content-between">
        <h3 className="mb-0 card-header-title">{test.data?.json.name || area.data?.json.name}</h3>
      </CardHeader>
      <CardBody>
        <ChartOverViewTable data={overviewTableData} theme={area.data?.json.identifier} />
        {!!responsesWithNAStanines?.length && (
          <Alert variant="warning">
            This report contains {responsesWithNAStanines.length}{' '}
            {pluralise('response', responsesWithNAStanines.length)} with a N/A stanine, these
            responses will not be shown in the following chart.
          </Alert>
        )}
        <table className="stanine-report-table">
          <tbody>
            <tr className="user-row">
              <td />
              {STANINES.map((s) => (
                <td key={s}>
                  <UsersWidget
                    responses={reportDataGrouped[s]}
                    splitByGender={!!genders?.length}
                    studentsPerIcon={studentsPerIcon}
                    showIndividualReportLink={showIndividualReportLinks}
                    generateAssessmentUserLink={generateAssessmentUserLink}
                  />
                </td>
              ))}
            </tr>
            <tr className="scale-row">
              <td>Stanine</td>
              {STANINES.map((s) => (
                <td key={s} style={{ backgroundColor: percent2color(s * 10) }}>
                  {s}
                </td>
              ))}
            </tr>

            <tr>
              <td>Percentage **</td>
              <td>
                {getPercentage(reportDataGrouped, [1])}%{isReferenceDataVisible && ' (4%)'}
              </td>
              <td colSpan={2}>
                {getPercentage(reportDataGrouped, [2, 3])}%{isReferenceDataVisible && ' (19%)'}
              </td>
              <td colSpan={3}>
                {getPercentage(reportDataGrouped, [4, 5, 6])}%{isReferenceDataVisible && ' (54%)'}
              </td>
              <td colSpan={2}>
                {getPercentage(reportDataGrouped, [7, 8])}%{isReferenceDataVisible && ' (19%)'}
              </td>
              <td>{getPercentage(reportDataGrouped, [9])}% (4%)</td>
            </tr>

            <tr>
              <td>Number of students</td>
              {STANINES.map((s) => (
                <td key={`${s}`}>{reportDataGrouped[s] ? reportDataGrouped[s].length : 0}</td>
              ))}
            </tr>
          </tbody>
          <tfoot className="table-footer">
            <tr>
              <td colSpan={10}>
                <StanineFooterLegend genders={genders} studentsPerIcon={studentsPerIcon} />
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="report-notes">
          {isReferenceDataVisible && (
            <div>
              * Numbers in brackets provide statistics for all students in the national reference
              group.
            </div>
          )}
          <div>** Percentages may not add up to 100 percent due to rounding.</div>
        </div>
      </CardBody>
      <ReportFooter theme={area.data?.json.identifier} period={period} />
    </Card>
  );
}
